import React, { memo } from 'react'
import Image from '@v4/components/common/Image'
import { Box, Grid } from '@material-ui/core'
import EditableHTML from '@v4/components/common/EditableHTML'
import ActionButton from '@v4/components/common/Buttons/ActionButton'
import PropTypes from 'prop-types'
import PageMetaData from '@v4/utils/data/PageMetaData'
import SortHandler from '@v4/components/common/SortHandler'
import EditableContainer from '@v4/components/common/EditableContainer'
import clsx from 'clsx'
import { CONTENT_WIDTH } from './Content.const'
import { useContent } from './Content.hooks'

const Content = props => {
	const [
		{
			classes,
			withBgImage,
			direction,
			image,
			withMobileImage,
			textShadow,
			text,
			textConfig,
			buttonText,
			contentWidth,
			buttonLink,
			buttonAction,
			extraShortcuts,
			textEditable,
			buttonTextConfig,
			buttonTextEditable,
			buttonTextKey,
			textKey,
		},
		{ handleTextClick, handleTextChange, handleButtonClick, handleImageClick, handleButtonTextChange },
	] = useContent(props)

	return (
		<>
			{withBgImage && (
				<Image
					className={clsx(classes.background, 'banner1-bgImage')}
					metaData={image}
					onClick={handleImageClick}
					isBackground
				/>
			)}
			<Grid className={classes.root} container>
				<SortHandler key="desktop" direction={direction}>
					<Grid className={classes.grid} item md={CONTENT_WIDTH[contentWidth]} xs={12}>
						<div className={clsx(classes.content, 'banner1-content')}>
							<EditableContainer
								visible={!!text}
								white={withBgImage}
								fieldConfig={textConfig}
								onAdd={handleTextChange}
								editable={textEditable}
							>
								<Box>
									<EditableHTML
										key={textKey}
										className={classes.text}
										gutterBottom
										white={withBgImage}
										html={text}
										onClick={handleTextClick}
										extraShortcuts={extraShortcuts}
										onChange={handleTextChange}
										editable={textEditable}
										shadow={withBgImage}
										textShadow={textShadow}
									/>
								</Box>
							</EditableContainer>
							<EditableContainer
								visible={!!buttonText}
								white={withBgImage}
								fieldConfig={buttonTextConfig}
								onAdd={handleButtonTextChange}
								editable={buttonTextEditable}
								label="Button"
								wrapper={({ children }) => (
									<Box
										className={clsx(classes.actionContainer, 'banner1-action-container')}
										display="flex"
										justifyContent="center"
										mt={4}
									>
										{children}
									</Box>
								)}
							>
								<ActionButton
									key={buttonTextKey}
									action={buttonAction}
									href={buttonLink}
									className={classes.button}
									onClick={handleButtonClick}
									editable={buttonTextEditable}
									onChange={handleButtonTextChange}
								>
									{buttonText}
								</ActionButton>
							</EditableContainer>
						</div>
					</Grid>
					{withMobileImage && (
						<Grid className="banner1-image-grid" xs={12} item>
							<Box className={classes.mobileImageWrapper}>
								<Image
									className={clsx(classes.mobileImage, 'banner1-image-mobile')}
									metaData={image}
									onClick={handleImageClick}
								/>
							</Box>
						</Grid>
					)}
				</SortHandler>
			</Grid>
		</>
	)
}

Content.propTypes = {
	desktopImage: PropTypes.instanceOf(PageMetaData),
	mobileImage: PropTypes.instanceOf(PageMetaData),
	buttonAction: PropTypes.string,
	buttonLink: PropTypes.string,
	buttonText: PropTypes.string,
	buttonTextKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	classes: PropTypes.shape({
		root: PropTypes.string,
		grid: PropTypes.string,
		text: PropTypes.string,
		actionContainer: PropTypes.string,
	}),
	contentWidth: PropTypes.string,
	onButtonClick: PropTypes.func,
	onImageClick: PropTypes.func,
	onMobileImageClick: PropTypes.func,
	onTextClick: PropTypes.func,
	onTextChange: PropTypes.func,
	onButtonTextChange: PropTypes.func,
	text: PropTypes.string,
	extraShortcuts: PropTypes.objectOf(PropTypes.string),
	opacity: PropTypes.number,
	mobileOpacity: PropTypes.number,
	textEditable: PropTypes.bool,
	textConfig: PropTypes.object,
	textKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	buttonTextEditable: PropTypes.bool,
	buttonTextConfig: PropTypes.object,
	backgroundImagePosition: PropTypes.string,
	mobileImagePosition: PropTypes.string,
	direction: PropTypes.string,
	mobileAlignment: PropTypes.string,
	mobileButtonWidth: PropTypes.string,
}

Content.defaultProps = {
	classes: {},
	buttonLink: null,
}

export default memo(Content)
